import type { Camera } from './types';
import type { Fence } from './fenceTypes';
import type { Building } from './buildingTypes';
import type { Gate } from './gateTypes';
import type { Note } from './noteTypes';
import type { Equipment } from './equipmentTypes';
import type { CableRoute } from './cableRouteTypes';
import type { FloorPlan } from './floorPlanTypes';
import type { Measurement } from './measurementTypes';
import type { Obstacle } from './obstacleTypes';
import { safeUUID } from '@/lib/safeUUID';

export interface Floor {
  id: string;
  name: string;
  order: number;
  floorPlan: FloorPlan | null;
  cameras: Camera[];
  fences: Fence[];
  buildings: Building[];
  gates: Gate[];
  equipment: Equipment[];
  notes: Note[];
  cableRoutes: CableRoute[];
  measurements: Measurement[];
  obstacles: Obstacle[];
}

export function createFloor(params: {
  id?: string;
  name: string;
  order: number;
  floorPlan?: FloorPlan | null;
  cameras?: Camera[];
  fences?: Fence[];
  buildings?: Building[];
  gates?: Gate[];
  equipment?: Equipment[];
  notes?: Note[];
  cableRoutes?: CableRoute[];
  measurements?: Measurement[];
  obstacles?: Obstacle[];
}): Floor {
  return {
    id: params.id ?? safeUUID(),
    name: params.name,
    order: params.order,
    floorPlan: params.floorPlan ?? null,
    cameras: params.cameras ?? [],
    fences: params.fences ?? [],
    buildings: params.buildings ?? [],
    gates: params.gates ?? [],
    equipment: params.equipment ?? [],
    notes: params.notes ?? [],
    cableRoutes: params.cableRoutes ?? [],
    measurements: params.measurements ?? [],
    obstacles: params.obstacles ?? [],
  };
}

/** Project-level settings persisted alongside floors[] in project.data. */
export interface DesignerProjectSettings {
  /** When false, the floor tab bar is hidden and the project behaves as
   *  single-floor. Users still have floors[0] internally but no UI for adding
   *  more floors. Default: false for backward compatibility. */
  multiFloorEnabled: boolean;
  /** When true, each floor numbers its cameras starting from 1. When false,
   *  numbers are continuous across every floor (flattened order). Default:
   *  true (matches historical per-floor behaviour). */
  cameraNumberingPerFloor: boolean;
  /** Compliance region for GDPR/regulatory frameworks. Default: 'eu'. */
  complianceRegion?: 'eu' | 'pl' | 'uk' | 'de' | 'fr' | 'es' | 'it' | 'us';
  /** Pinned ("sticky") placement tool: a single-shot insert (camera,
   *  equipment, note) leaves the tool armed instead of snapping back to
   *  select, so a run of cameras is one menu trip instead of twenty.
   *  Default: false, i.e. the historical one-object-per-pick behaviour. */
  toolSticky?: boolean;
  /**
   * Correct the reported reach for what the optics and the mount actually
   * deliver (see `lighting/mtf.ts` and `lighting/mastSway.ts`).
   *
   * Lives with the PROJECT and not with the view, deliberately. It changes the
   * numbers a report prints, so a design saved with it on and reopened by
   * someone whose toggle happens to be off would quote two different reaches
   * for the same drawing with nothing on the page to say why. Absent means off,
   * which is what every project made before this existed keeps.
   */
  opticalCorrection?: boolean;
  /**
   * Wind at the site, m/s, for the mast sway model.
   *
   * A published pole deflection belongs to the wind speed it was computed at,
   * so without this figure a mast contributes nothing at all rather than a
   * default amount.
   */
  siteWindMs?: number;
  /**
   * Fallback for a camera that has not chosen its own `labelStyle`: the
   * numbered disc, the bare number, its text label, or nothing. Absent means
   * the disc, which is what every earlier project shows.
   */
  cameraLabelStyle?: CameraLabelStyle;
  /**
   * Size of the camera numbers. 'auto' (and absent) follows each camera's
   * icon size, as numbers always did; the fixed sizes ignore it.
   */
  cameraLabelSize?: CameraLabelSize;
  /**
   * How coverage looks where cones overlap. 'blend' (and absent) lets the
   * translucent fills stack, so shared ground reads darker and mixes colours,
   * the look every earlier project has. 'single' paints the union once: shared
   * ground looks like the coverage of one camera.
   */
  coverageOverlap?: CoverageOverlapStyle;
  /** What the cable schedules print as conduit (cableConduit.ts): the
   *  cable's recommended containment where a run names none (and absent),
   *  only what each run names, or nothing when the project uses no conduit. */
  conduitMode?: import('./cableConduit').ConduitMode;
  /** The coverage heat-map: 'heat' (and absent) colours ground by how many
   *  cameras see it; 'single' shows all coverage in one colour. */
  coverageHeatmapStyle?: 'heat' | 'single';
  /** Copies of the team's own equipment placed in this project (ids without
   *  the 'custom-' prefix), so the project shows it to people outside the
   *  team too. Added on placement, refreshed from the library, checked again
   *  on load. */
  customEquipment?: import('@/lib/customEquipment').CustomEquipmentItem[];
  /** Ground the AI camera planner is asked to cover (a car park, a yard, a
   *  drive), drawn in its own window and kept for the next run. */
  plannerAreas?: PlannerArea[];
  /** Ground painted with the requirement brush, numbered, each with the DORI
   *  level it must be seen at (ai/requirementAreas.ts). */
  requirementAreas?: import('./ai/requirementAreas').RequirementArea[];
  /** The network topology diagram as arranged in its editor: positions,
   *  colours, notes, areas and the labels shown (topology/topologyTypes.ts). */
  topology?: import('./topology/topologyTypes').TopologyDoc;
}

export interface PlannerArea {
  id: string;
  points: { lat: number; lng: number }[];
  /** The DORI level that ground has to be seen at. */
  tier: 'detect' | 'observe' | 'recognize' | 'identify';
}

export type CoverageOverlapStyle = 'blend' | 'single';

export type CameraLabelStyle = 'badge' | 'text' | 'label' | 'none';
export type CameraLabelSize = 'auto' | 'small' | 'medium' | 'large';

export const DEFAULT_PROJECT_SETTINGS: DesignerProjectSettings = {
  multiFloorEnabled: false,
  cameraNumberingPerFloor: true,
  complianceRegion: 'eu',
  toolSticky: false,
};

export interface LegacyProjectEntities {
  floorPlan?: FloorPlan | null;
  cameras?: Camera[];
  fences?: Fence[];
  buildings?: Building[];
  gates?: Gate[];
  equipment?: Equipment[];
  notes?: Note[];
  cableRoutes?: CableRoute[];
  measurements?: Measurement[];
  obstacles?: Obstacle[];
}

export function wrapLegacyAsFloors(
  legacy: LegacyProjectEntities,
  defaultName = 'Floor 1',
): { floors: Floor[]; activeFloorId: string } {
  const floor = createFloor({
    name: defaultName,
    order: 0,
    floorPlan: legacy.floorPlan ?? null,
    cameras: legacy.cameras ?? [],
    fences: legacy.fences ?? [],
    buildings: legacy.buildings ?? [],
    gates: legacy.gates ?? [],
    equipment: legacy.equipment ?? [],
    notes: legacy.notes ?? [],
    cableRoutes: legacy.cableRoutes ?? [],
    measurements: legacy.measurements ?? [],
    obstacles: legacy.obstacles ?? [],
  });
  return { floors: [floor], activeFloorId: floor.id };
}

export function hasLegacyShape(data: unknown): boolean {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  if (Array.isArray(d.floors) && d.floors.length > 0) return false;
  return (
    'cameras' in d ||
    'fences' in d ||
    'buildings' in d ||
    'gates' in d ||
    'equipment' in d ||
    'notes' in d ||
    'cableRoutes' in d ||
    'floorPlan' in d ||
    'measurements' in d ||
    'obstacles' in d
  );
}

/** Loose input — accepts either the modern `{ floors }` or legacy flat shape,
 *  including the strict `CCTVProjectData` and the offer editors' looser props.
 *  `any[]` (not `unknown[]`) so `Floor[]`/`Gate[]` callers assign cleanly and
 *  the internal `f[key]` indexing type-checks. */
interface ProjectEntitySource {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  floors?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cameras?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fences?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  buildings?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  gates?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  equipment?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notes?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cableRoutes?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obstacles?: any[];
}

/**
 * Flatten a project's entities from EITHER shape — modern `{ floors: [...] }`
 * or legacy flat `{ cameras, equipment, … }`. New projects keep everything
 * under `floors[]`, so any consumer that reads the top-level arrays directly
 * (offer editor, offer PDF export) sees ZERO unless it goes through here.
 *
 * Returns concrete arrays (never undefined) so callers can `.length` / `.map`
 * without guards.
 */
export function flattenProjectEntities(data: ProjectEntitySource | null | undefined): {
  cameras: Camera[];
  fences: Fence[];
  buildings: Building[];
  gates: Gate[];
  equipment: Equipment[];
  notes: Note[];
  cableRoutes: CableRoute[];
  obstacles: Obstacle[];
} {
  const floors = data?.floors;
  if (Array.isArray(floors) && floors.length > 0) {
    const pick = <T>(key: string): T[] =>
      floors.flatMap((f) => ((f?.[key] as T[] | undefined) ?? []));
    return {
      cameras: pick<Camera>('cameras'),
      fences: pick<Fence>('fences'),
      buildings: pick<Building>('buildings'),
      gates: pick<Gate>('gates'),
      equipment: pick<Equipment>('equipment'),
      notes: pick<Note>('notes'),
      cableRoutes: pick<CableRoute>('cableRoutes'),
      obstacles: pick<Obstacle>('obstacles'),
    };
  }
  return {
    cameras: (data?.cameras as Camera[]) ?? [],
    fences: (data?.fences as Fence[]) ?? [],
    buildings: (data?.buildings as Building[]) ?? [],
    gates: (data?.gates as Gate[]) ?? [],
    equipment: (data?.equipment as Equipment[]) ?? [],
    notes: (data?.notes as Note[]) ?? [],
    cableRoutes: (data?.cableRoutes as CableRoute[]) ?? [],
    obstacles: (data?.obstacles as Obstacle[]) ?? [],
  };
}
